import { fromPromise } from 'pulse/adapters';
import { defineMutation } from 'pulse/mutations';
import { useMutation, useResource } from 'pulse/react';
import { defineResource } from 'pulse/resources';
import { toSuccess } from 'pulse/resolvers';

import { getLocalStore } from '../utils/superstore';

type LocalStoreValue = {
  key: string;
  value: string | null;
};

type ReadLocalStoreParams = {
  key: string;
};

const LocalStoreValueResource = defineResource({
  typeName: 'framework-listing-lib:LocalStoreValue',
  args: ['key'],
  resolver: fromPromise(
    'readLocalStore',
    ({ key }: ReadLocalStoreParams): Promise<LocalStoreValue> =>
      getLocalStore()
        .get(key)
        .then((value) => {
          return {
            key,
            value,
          };
        })
  ),
}).register();

type WriteLocalStoreParams = {
  key: string;
  value: string;
};

const WriteLocalStore = defineMutation({
  typeName: 'framework-listing-lib:WriteLocalStore',
  async mutation({
    key,
    value,
  }: WriteLocalStoreParams): Promise<LocalStoreValue> {
    const result = await getLocalStore().set(key, value);

    return {
      key,
      value: result,
    };
  },
  optimistic({ params, setState }) {
    setState({
      resource: LocalStoreValueResource,
      params: { key: params.key },
      state: toSuccess<LocalStoreValue>({
        key: params.key,
        value: params.value,
      }),
    });
  },
  resolved({ result, setState, invalidate }) {
    if (result.value === null) {
      invalidate({
        resource: LocalStoreValueResource,
        params: { key: result.key },
      });
      return;
    }

    setState({
      resource: LocalStoreValueResource,
      params: { key: result.key },
      state: toSuccess(result),
    });
  },
});

export function useReadLocalStore(
  key: string,
  options?: {
    skip?: boolean;
  }
) {
  'use memo';
  return useResource(LocalStoreValueResource, {
    params: { key },
    skip: options?.skip,
  });
}

export function useWriteLocalStore() {
  'use memo';
  return useMutation(WriteLocalStore);
}
